import type { MimeTypeMap } from "@/types/fileUpload"
import { NOTE_MAX_FILE_BYTES, NOTE_MAX_FILES } from "../../../api/notesApi"

// Same set the server accepts. The server decides from the file's bytes and has the
// final word: this only spares the user an upload that is bound to be refused.
export const NOTE_FILE_TYPES: MimeTypeMap = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "application/pdf": [".pdf"],
}

const TYPE_BY_EXTENSION: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  pdf: "application/pdf",
}

/** The reported type, else the one the file name implies (some drops report none). */
function noteFileType(file: File): string | null {
  if (file.type) return file.type in NOTE_FILE_TYPES ? file.type : null
  const extension = file.name.split(".").pop()?.toLowerCase() ?? ""
  return TYPE_BY_EXTENSION[extension] ?? null
}

export type NoteFileProblem = "unsupported" | "tooLarge" | "limit"

/**
 * Splits incoming files into the ones that fit and the reasons for the rest.
 * `currentCount` is every file the note would already carry (the ones staged plus,
 * when editing, the ones it has stored), because the limit counts both.
 */
export function screenNoteFiles(
  incoming: File[],
  currentCount: number,
): { accepted: File[]; problems: NoteFileProblem[] } {
  const accepted: File[] = []
  const problems = new Set<NoteFileProblem>()
  for (const file of incoming) {
    if (!noteFileType(file)) problems.add("unsupported")
    else if (file.size > NOTE_MAX_FILE_BYTES) problems.add("tooLarge")
    else if (currentCount + accepted.length >= NOTE_MAX_FILES)
      problems.add("limit")
    else accepted.push(file)
  }
  return { accepted, problems: [...problems] }
}

/**
 * The files on a paste, or [] when it is a text paste. A screenshot arrives as a
 * file on `files` (or only on `items`, in some browsers). A paste that also carries
 * text (cells copied from a sheet bring a picture along) is a text paste: the file
 * is not what the user meant to attach.
 */
export function clipboardFiles(data: DataTransfer | null): File[] {
  if (!data || data.getData("text/plain")) return []
  const files = Array.from(data.files)
  if (files.length > 0) return files
  return Array.from(data.items ?? [])
    .filter((item) => item.kind === "file")
    .flatMap((item) => item.getAsFile() ?? [])
}
